import React, { useState } from 'react';
import { Col, Container, Row, Button, Card, Spinner, Modal, Alert } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faKey,
    faArrowLeft,
    faCopy,
    faCheckCircle,
    faExclamationTriangle,
    faRedo,
    faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import { getRequest, postRequest } from '../../../../Helper/ApiClient';
import CodeBlock from '../../../../Abstracts/CodeBlock/CodeBlock';

export default function Key() {
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [copied, setCopied] = useState(false);

    const apiKeyRequest = getRequest('/api/api_settings');
    const apiKeyMutation = postRequest('/api/api_settings/generate_api_key');

    const hasApiKey = apiKeyRequest.isSuccess && apiKeyRequest.data?.api_key?.length > 0;
    const apiKey = apiKeyRequest.data?.api_key || null;

    const handleGenerateKey = () => {
        if (hasApiKey) {
            setShowConfirmModal(true);
        } else {
            generateNewKey();
        }
    };

    const generateNewKey = () => {
        setShowConfirmModal(false);
        apiKeyMutation.mutate(new FormData(), {
            onSuccess: () => apiKeyRequest.refetch(),
        });
    };

    const copyToClipboard = () => {
        if (apiKey) {
            navigator.clipboard.writeText(apiKey);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return (
        <Container className="py-4">
            {/* Header */}
            <Row className="mb-4">
                <Col>
                    <NavLink
                        to="/service-center/api"
                        className="text-decoration-none text-muted d-inline-flex align-items-center mb-3 text-14"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                        Back to API Overview
                    </NavLink>
                    <h1 className="mb-2">
                        <FontAwesomeIcon icon={faKey} className="me-3 text-primary" />
                        API Key
                    </h1>
                    <p className="text-muted mb-0">
                        Manage your API key for authenticating REST API requests.
                    </p>
                </Col>
            </Row>

            {/* API Key Card */}
            <Row className="mb-4">
                <Col md={8}>
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="p-4">
                            {apiKeyRequest.isPending ? (
                                <div className="text-center py-4">
                                    <Spinner animation="border" className="mb-2" />
                                    <div className="text-muted">Loading...</div>
                                </div>
                            ) : hasApiKey ? (
                                <>
                                    <div className="d-flex align-items-center mb-3">
                                        <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
                                        <span className="fw-semibold">Your API Key is active</span>
                                    </div>
                                    <div
                                        className="d-flex align-items-center gap-2 p-3 rounded mb-3"
                                        style={{ backgroundColor: '#f8f9fa', fontFamily: 'monospace' }}
                                    >
                                        <code className="flex-grow-1 text-14">
                                            {apiKey}
                                        </code>
                                        <Button
                                            variant={copied ? 'success' : 'outline-secondary'}
                                            size="sm"
                                            onClick={copyToClipboard}
                                        >
                                            <FontAwesomeIcon icon={copied ? faCheckCircle : faCopy} className="me-1" />
                                            {copied ? 'Copied!' : 'Copy'}
                                        </Button>
                                    </div>
                                    <Button
                                        variant="outline-warning"
                                        size="sm"
                                        onClick={handleGenerateKey}
                                        disabled={apiKeyMutation.isPending}
                                    >
                                        <FontAwesomeIcon icon={faRedo} className="me-2" />
                                        {apiKeyMutation.isPending ? 'Generating...' : 'Regenerate Key'}
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <div className="text-center py-3">
                                        <FontAwesomeIcon icon={faKey} size="3x" className="text-muted mb-3" />
                                        <h2>No API Key Generated</h2>
                                        <p className="text-muted mb-4">
                                            Generate an API key to authenticate your REST API requests.
                                        </p>
                                        <Button
                                            variant="primary"
                                            onClick={handleGenerateKey}
                                            disabled={apiKeyMutation.isPending}
                                        >
                                            <FontAwesomeIcon icon={faKey} className="me-2" />
                                            {apiKeyMutation.isPending ? 'Generating...' : 'Generate API Key'}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Usage Instructions */}
            <Row className="mb-4">
                <Col md={8}>
                    <Card className="border-0" style={{ backgroundColor: '#f8f9fa', borderRadius: 12 }}>
                        <Card.Body className="p-4">
                            <h2 className="mb-3">
                                <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-primary" />
                                How to Use Your API Key
                            </h2>
                            <p className="text-14">
                                Include your API key in the request header when calling our REST API:
                            </p>
                            <div className="mb-3">
                                <CodeBlock language="http" title="HTTP Header">
                                    {`X-Authorization: ${apiKey || 'your-api-key'}`}
                                </CodeBlock>
                            </div>
                            <CodeBlock language="bash" title="Example cURL request">
                                {`curl -H "X-Authorization: ${apiKey || 'your-api-key'}" \\
  https://rest.naskorsports.com/customer-api/v1/articles`}
                            </CodeBlock>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Documentation Link */}
            <Row>
                <Col md={8}>
                    <Alert variant="info" className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faInfoCircle} className="me-3" />
                        <div>
                            <strong>API Documentation:</strong>{' '}
                            View the full API documentation in our{' '}
                            <NavLink to="/service-center/api/rest">
                                interactive API docs
                            </NavLink>
                        </div>
                    </Alert>
                </Col>
            </Row>

            {/* Confirmation Modal */}
            <Modal centered show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-warning" />
                        Regenerate API Key?
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Are you sure you want to regenerate your API key? This action will:
                    </p>
                    <ul>
                        <li><strong>Invalidate</strong> your current API key immediately</li>
                        <li>All existing integrations using the old key will stop working</li>
                        <li>You will need to update the key in all your systems</li>
                    </ul>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="warning" onClick={generateNewKey}>
                        <FontAwesomeIcon icon={faRedo} className="me-2" />
                        Yes, Regenerate Key
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}
